import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import {
  getMyBartenderOnboarding,
  saveBartenderOnboarding,
} from "@/lib/bartender-onboarding.functions";
import type { VerifiedAddress } from "@/lib/places.functions";

export const Route = createFileRoute("/_authenticated/bartender/onboarding")({
  head: () => ({
    meta: [{ title: "Bartender Onboarding — The Hospitality Reserve" }],
  }),
  component: BartenderOnboardingWizard,
});

type StepKey = "identity" | "contact" | "address" | "review";
const STEPS: { key: StepKey; label: string }[] = [
  { key: "identity", label: "Identity" },
  { key: "contact", label: "Contact" },
  { key: "address", label: "Address" },
  { key: "review", label: "Review" },
];

function BartenderOnboardingWizard() {
  const navigate = useNavigate();
  const load = useServerFn(getMyBartenderOnboarding);
  const save = useServerFn(saveBartenderOnboarding);

  const [hydrating, setHydrating] = useState(true);
  const [stepIdx, setStepIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [fullName, setFullName] = useState("");
  const [dob, setDob] = useState("");
  const [dobLocked, setDobLocked] = useState(false);
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState<VerifiedAddress | null>(null);
  const [initialAddressQuery, setInitialAddressQuery] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    (async () => {
      const r = await load();
      if ("error" in r) {
        toast.error(r.error);
        navigate({ to: "/dashboard", replace: true });
        return;
      }
      const p = r.profile;
      // Already complete AND HMRC identity details on file → nothing to do here.
      if (p.bartender_onboarding_completed_at && p.date_of_birth) {
        navigate({ to: "/dashboard", replace: true });
        return;
      }
      setFullName(p.full_name ?? "");
      if (p.date_of_birth) {
        setDob(String(p.date_of_birth).slice(0, 10));
        setDobLocked(true);
      }
      setPhone(p.phone_number ?? "");
      if (p.address_line1) {
        setAddress({
          place_id: p.place_id ?? "",
          formatted: [p.address_line1, p.address_line2, p.city, p.postcode]
            .filter(Boolean)
            .join(", "),
          address_line1: p.address_line1,
          address_line2: p.address_line2 ?? "",
          city: p.city ?? "",
          postcode: p.postcode ?? "",
          country: p.country ?? "United Kingdom",
          latitude: null,
          longitude: null,
        });
      } else if (p.address) {
        setInitialAddressQuery(p.address);
      }
      setHydrating(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const step = STEPS[stepIdx].key;

  const validateStep = (): boolean => {
    const e: Record<string, string> = {};
    if (step === "identity") {
      if (fullName.trim().length < 2) e.fullName = "Your full legal name is required.";
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
        e.dob = "Enter your date of birth.";
      } else {
        const eighteen = new Date();
        eighteen.setFullYear(eighteen.getFullYear() - 18);
        if (new Date(`${dob}T00:00:00`) > eighteen) e.dob = "You must be at least 18 years old.";
      }
    }

    if (step === "contact" && !/^[0-9+()\-\s]{7,}$/.test(phone.trim())) {
      e.phone = "Enter a valid phone number.";
    }
    if (step === "address" && !address) {
      e.address = "Pick your home address from the verified suggestions.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  };
  const handleBack = () => setStepIdx((i) => Math.max(i - 1, 0));

  const handleSubmit = async () => {
    if (!address) return;
    setSubmitting(true);
    try {
      const r = await save({
        data: {
          full_name: fullName.trim(),
          date_of_birth: dob,
          phone_number: phone.trim(),
          address_line1: address.address_line1,
          address_line2: address.address_line2 || null,
          city: address.city,
          postcode: address.postcode,
          country: address.country || "United Kingdom",
          place_id: address.place_id || null,
        },
      });
      if ("error" in r) throw new Error(r.error);
      toast.success("Profile saved. Next: upload your CV.");
      navigate({ to: "/bartender/application", replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save your details.");
    } finally {
      setSubmitting(false);
    }
  };

  if (hydrating) {
    return <div className="min-h-dvh bg-background" aria-busy="true" />;
  }

  return (
    <div className="min-h-dvh bg-background">
      <ReserveHeader role="bartender" />
      <main className="mx-auto max-w-2xl px-6 py-12">
        <p className="tracking-wide-luxury text-accent">Bartender Onboarding</p>
        <h1 className="mt-3 font-display text-3xl">Let's set up your profile</h1>
        <span className="gold-divider mt-4" />
        <p className="mt-3 text-sm text-muted-foreground">
          We need these details before you upload your CV so we can match
          you to shifts nearby and pay you on time.
        </p>

        <ol
          aria-label="Onboarding steps"
          className="mt-8 grid grid-cols-4 gap-2 text-xs tracking-wide-luxury"
        >
          {STEPS.map((s, i) => (
            <li
              key={s.key}
              aria-current={i === stepIdx ? "step" : undefined}
              className={`border-t-2 pt-3 ${
                i <= stepIdx ? "border-accent text-accent" : "border-border text-muted-foreground"
              }`}
            >
              <span className="block text-[10px]">Step {i + 1}</span>
              {s.label}
              <span className="sr-only">
                {i < stepIdx ? " (completed)" : i === stepIdx ? " (current step)" : " (not started)"}
              </span>
            </li>
          ))}
        </ol>

        <p aria-live="polite" className="sr-only">
          Step {stepIdx + 1} of {STEPS.length}: {STEPS[stepIdx].label}
        </p>

        <section
          aria-labelledby="onboarding-step-heading"
          className="mt-8 border border-border bg-card p-6 space-y-5"
        >
          <h2 id="onboarding-step-heading" className="sr-only">
            {STEPS[stepIdx].label}
          </h2>
          {step === "identity" && (
            <div className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full legal name</Label>
                <Input
                  id="full_name"
                  aria-invalid={Boolean(errors.fullName)}
                  aria-describedby={errors.fullName ? "full_name-error" : undefined}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  maxLength={120}
                  placeholder="As shown on your right-to-work documents"
                  className="rounded-none"
                />
                {errors.fullName && <p id="full_name-error" role="alert" className="text-xs text-destructive">{errors.fullName}</p>}
                <p className="text-xs text-muted-foreground">
                  This must match the name on your right-to-work documents.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dob">Date of birth</Label>
                <Input
                  id="dob"
                  aria-invalid={Boolean(errors.dob)}
                  aria-describedby={errors.dob ? "dob-error" : undefined}
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  disabled={dobLocked}
                  className="rounded-none"
                />
                {errors.dob && <p id="dob-error" role="alert" className="text-xs text-destructive">{errors.dob}</p>}
                <p className="text-xs text-muted-foreground">
                  You must be 18 or over to serve alcohol in the UK.
                </p>
              </div>

            </div>
          )}


          {step === "contact" && (
            <div className="space-y-2">
              <Label htmlFor="phone">Mobile number</Label>
              <Input
                id="phone"
                aria-invalid={Boolean(errors.phone)}
                aria-describedby={errors.phone ? "phone-error" : undefined}
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                maxLength={32}
                placeholder="+44 7700 900123"
                className="rounded-none"
              />
              {errors.phone && <p id="phone-error" role="alert" className="text-xs text-destructive">{errors.phone}</p>}
              <p className="text-xs text-muted-foreground">
                Used only for shift confirmations and urgent on-the-night contact from the venue.
              </p>
            </div>
          )}

          {step === "address" && (
            <>
              <AddressAutocomplete
                label="Home address"
                initialQuery={initialAddressQuery}
                initialVerified={address}
                onVerified={setAddress}
                disabled={submitting}
              />
              {errors.address && <p role="alert" className="text-xs text-destructive">{errors.address}</p>}
              {address && (
                <div className="mt-3 border border-border bg-muted/20 p-3 text-xs text-muted-foreground space-y-0.5">
                  <p>{address.address_line1}</p>
                  {address.address_line2 && <p>{address.address_line2}</p>}
                  <p>{address.city} {address.postcode}</p>
                </div>
              )}
            </>
          )}

          {step === "review" && (
            <dl className="space-y-3 text-sm">
              <Row label="Name" value={fullName} />
              <Row label="Date of birth" value={dob} />
              <Row label="Phone" value={phone} />
              <Row label="Address" value={address?.formatted ?? ""} />
              <Row label="Postcode" value={address?.postcode ?? ""} />
              <p className="pt-2 text-xs text-muted-foreground">
                You'll move on to uploading your CV after saving — we check your experience automatically.
              </p>
            </dl>
          )}
        </section>

        <div className="mt-6 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={stepIdx === 0 || submitting}
            className="rounded-none tracking-wide-luxury"
          >
            Back
          </Button>
          {step !== "review" ? (
            <Button
              onClick={handleNext}
              className="rounded-none bg-accent text-accent-foreground hover:bg-accent/90 tracking-wide-luxury"
            >
              Continue
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting || !address}
              className="rounded-none bg-accent text-accent-foreground hover:bg-accent/90 tracking-wide-luxury"
            >
              {submitting ? "Saving…" : "Save & upload CV"}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border pb-2">
      <dt className="text-xs tracking-wide-luxury text-muted-foreground">{label}</dt>
      <dd className="text-right">{value || <span className="text-muted-foreground">—</span>}</dd>
    </div>
  );
}
